// Per-element cleanup state — keyed weakly so GC handles removed nodes.
const _carouselState = new WeakMap();
const _heroState     = new WeakMap();
const _showcaseState = new WeakMap();
const _accordionState = new WeakMap();
const _busyState      = new WeakMap();

// =============================================================================
// #region cards/cards.js
// =============================================================================

/**
 * Cards carousel — horizontal scrolling with JS-driven snap.
 * After any touch/mouse swipe ends, the track smoothly rounds
 * to the nearest card boundary. No CSS scroll-snap needed.
 */

function initCards(root) {
    (root || document).querySelectorAll('[data-carousel]').forEach((carousel) => {
        // Tear down listeners from any previous init on this element.
        if (_carouselState.has(carousel)) {
            _carouselState.get(carousel).abort();
        }
        const controller = new AbortController();
        const { signal } = controller;
        _carouselState.set(carousel, controller);

        const track   = carousel.querySelector('[data-carousel-track]');
        const prevBtn = carousel.querySelector('[data-carousel-prev]');
        const nextBtn = carousel.querySelector('[data-carousel-next]');
        if (!track) return;

        let snapTimer = null;

        function getCardWidth() {
            const firstCard = track.querySelector('[data-carousel-item]');
            if (!firstCard) return 300;
            const gap = parseFloat(getComputedStyle(track).gap) || 0;
            return firstCard.offsetWidth + gap;
        }

        function snapToNearest() {
            const cardWidth = getCardWidth();
            const nearest = Math.round(track.scrollLeft / cardWidth) * cardWidth;
            track.scrollTo({ left: nearest, behavior: 'smooth' });
        }

        function scheduleSnap() {
            clearTimeout(snapTimer);
            snapTimer = setTimeout(snapToNearest, 120);
        }

        function updateArrows() {
            if (!prevBtn || !nextBtn) return;
            const atStart = track.scrollLeft <= 1;
            const atEnd   = track.scrollLeft + track.offsetWidth >= track.scrollWidth - 1;

            prevBtn.disabled      = atStart;
            prevBtn.style.opacity = atStart ? '0.5' : '1';
            nextBtn.disabled      = atEnd;
            nextBtn.style.opacity = atEnd ? '0.5' : '1';
        }

        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                clearTimeout(snapTimer);
                track.scrollBy({ left: -getCardWidth(), behavior: 'smooth' });
            }, { signal });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                clearTimeout(snapTimer);
                track.scrollBy({ left: getCardWidth(), behavior: 'smooth' });
            }, { signal });
        }

        track.addEventListener('scroll', () => {
            updateArrows();
            scheduleSnap();
        }, { passive: true, signal });

        updateArrows();
    });
}

// =============================================================================
// #endregion cards/cards.js
// =============================================================================


// =============================================================================
// #region hero/hero.js
// =============================================================================

/**
 * Hero carousel — crossfade between slides with auto-advance.
 * Pauses when the user hovers or focuses within the hero.
 * Arrows and dots give manual control.
 */

function initHero(root) {
    (root || document).querySelectorAll('[data-hero-carousel]').forEach((hero) => {
        // Tear down listeners and stop the autoplay timer from any previous init.
        if (_heroState.has(hero)) {
            const prev = _heroState.get(hero);
            prev.controller.abort();
            if (prev.timer) clearInterval(prev.timer);
        }
        const controller = new AbortController();
        const { signal } = controller;
        const state = { controller, timer: null };
        _heroState.set(hero, state);

        const slides  = hero.querySelectorAll('[data-hero-slide]');
        const dots    = hero.querySelectorAll('[data-hero-dot]');
        const prevBtn = hero.querySelector('[data-hero-prev]');
        const nextBtn = hero.querySelector('[data-hero-next]');
        if (slides.length === 0) return;

        let current = 0;
        const INTERVAL = 6000;

        function goTo(index) {
            if (index < 0) index = slides.length - 1;
            if (index >= slides.length) index = 0;

            slides[current].classList.remove('hero__slide--active');
            if (dots[current]) {
                dots[current].classList.remove('hero__dot--active');
                dots[current].setAttribute('aria-selected', 'false');
            }

            current = index;
            slides[current].classList.add('hero__slide--active');
            if (dots[current]) {
                dots[current].classList.add('hero__dot--active');
                dots[current].setAttribute('aria-selected', 'true');
            }

            if (prevBtn) prevBtn.disabled = false;
            if (nextBtn) nextBtn.disabled = false;
        }

        function next() { goTo(current + 1); }
        function prev() { goTo(current - 1); }

        function startAutoPlay() {
            stopAutoPlay();
            state.timer = setInterval(next, INTERVAL);
        }

        function stopAutoPlay() {
            if (state.timer) {
                clearInterval(state.timer);
                state.timer = null;
            }
        }

        if (nextBtn) nextBtn.addEventListener('click', () => { next(); startAutoPlay(); }, { signal });
        if (prevBtn) prevBtn.addEventListener('click', () => { prev(); startAutoPlay(); }, { signal });

        dots.forEach((dot, i) => {
            dot.addEventListener('click', () => { goTo(i); startAutoPlay(); }, { signal });
        });

        hero.addEventListener('mouseenter', stopAutoPlay,  { signal });
        hero.addEventListener('mouseleave', startAutoPlay, { signal });
        hero.addEventListener('focusin',    stopAutoPlay,  { signal });
        hero.addEventListener('focusout',   startAutoPlay, { signal });

        hero.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft')  { prev(); startAutoPlay(); }
            if (e.key === 'ArrowRight') { next(); startAutoPlay(); }
        }, { signal });

        if (prevBtn) prevBtn.disabled = false;
        startAutoPlay();
    });
}

// =============================================================================
// #endregion hero/hero.js
// =============================================================================


// =============================================================================
// #region showcase/showcase.js
// =============================================================================

/* Showcase carousel — fade between cards on arrow click.
   Initialises every .showcase instance on the page. */

function initShowcase(root) {
    (root || document).querySelectorAll('.showcase').forEach((section) => {
        // Tear down listeners from any previous init on this element.
        if (_showcaseState.has(section)) {
            _showcaseState.get(section).abort();
        }
        const controller = new AbortController();
        const { signal } = controller;
        _showcaseState.set(section, controller);

        const cards   = section.querySelectorAll('.showcase__card');
        const prevBtn = section.querySelector('.showcase__arrow--prev');
        const nextBtn = section.querySelector('.showcase__arrow--next');
        if (!cards.length || !prevBtn || !nextBtn) return;

        let current = 0;

        function show(index) {
            cards[current].classList.remove('showcase__card--active');
            current = index;
            cards[current].classList.add('showcase__card--active');
            prevBtn.disabled = current === 0;
            nextBtn.disabled = current === cards.length - 1;
        }

        prevBtn.addEventListener('click', () => { if (current > 0) show(current - 1); }, { signal });
        nextBtn.addEventListener('click', () => { if (current < cards.length - 1) show(current + 1); }, { signal });
    });
}

// =============================================================================
// #endregion showcase/showcase.js
// =============================================================================


// =============================================================================
// #region accordion
// =============================================================================

/**
 * Accordion — click a title button to expand/collapse its content panel.
 * Exclusive: opening one item closes any currently open sibling.
 * Uses a delegated listener per widget so re-calling is safe via the WeakMap guard.
 */
function initAccordion(root) {
    (root || document).querySelectorAll('.accordion').forEach((accordion) => {
        if (_accordionState.has(accordion)) {
            _accordionState.get(accordion).abort();
        }
        const controller = new AbortController();
        const { signal } = controller;
        _accordionState.set(accordion, controller);

        accordion.addEventListener('click', (e) => {
            const btn = e.target.closest('.accordion__question');
            if (!btn) { return; }
            const isExpanded = btn.getAttribute('aria-expanded') === 'true';

            // Close all items first
            accordion.querySelectorAll('.accordion__question').forEach(b => {
                b.setAttribute('aria-expanded', 'false');
                const a = b.closest('.accordion__item')?.querySelector('.accordion__answer');
                if (a) { a.classList.remove('accordion__answer--open'); }
            });

            // Open this one only if it wasn't already open
            if (!isExpanded) {
                btn.setAttribute('aria-expanded', 'true');
                const answer = btn.closest('.accordion__item')?.querySelector('.accordion__answer');
                if (answer) { answer.classList.add('accordion__answer--open'); }
            }
        }, { signal });
    });
}

// =============================================================================
// #endregion accordion
// =============================================================================


// =============================================================================
// #region busy state
// =============================================================================

/**
 * Busy state for actions that take the member off the page — a form post or a
 * navigation. Opt in with data-busy:
 *
 *   <form data-busy>   the button that submitted it goes busy
 *   <a data-busy>      the link itself goes busy when clicked
 *
 * While busy the control keeps its exact size (the label is hidden, not removed)
 * and CSS blocks pointer events, so the action cannot be fired twice.
 *
 * Listeners are delegated from the root rather than the document, because submit
 * does not cross a shadow boundary (composed: false) — a document listener never
 * sees a form posted from inside a shadow root. Pass the shadow root to reach it.
 */
const BUSY_CLASS = 'is-busy';
const BUSY_LABEL_CLASS = 'btn__label';

// Submit events already acted on. When roots are nested — the document plus a
// shadow root or Froala element inside it — one submit reaches both listeners,
// and without this the second would mistake the first for a double submit and
// cancel it.
const _handledSubmits = new WeakSet();

function setBusy(control) {
    if (!control || control.classList.contains(BUSY_CLASS)) { return; }

    // Wrap the children so the label can be hidden while still occupying its box.
    // A raw text node cannot be hidden on its own, and removing it would resize
    // the control the moment it goes busy.
    if (!control.querySelector('.' + BUSY_LABEL_CLASS)) {
        // ownerDocument, not document — a node built by the wrong document is
        // adopted on insert, and inside a shadow root that is not always this one.
        const label = control.ownerDocument.createElement('span');
        label.className = BUSY_LABEL_CLASS;
        while (control.firstChild) { label.appendChild(control.firstChild); }
        control.appendChild(label);
    }

    control.classList.add(BUSY_CLASS);
    control.setAttribute('aria-busy', 'true');
}

function clearBusy(control) {
    control.classList.remove(BUSY_CLASS);
    control.removeAttribute('aria-busy');
}

/**
 * The element the event actually started on. event.target is retargeted to the
 * host once a composed event crosses out of a shadow tree, so the deepest entry
 * in the composed path is the only reliable starting point for closest().
 */
function eventOrigin(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : null;
    const origin = path && path.length ? path[0] : event.target;

    return origin && typeof origin.closest === 'function' ? origin : null;
}

function initBusyState(root) {
    const scope = root || document;

    // Tear down listeners from any previous init on this root.
    if (_busyState.has(scope)) {
        _busyState.get(scope).abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    _busyState.set(scope, controller);

    scope.addEventListener('submit', (e) => {
        const origin = eventOrigin(e);
        const form = origin ? origin.closest('form[data-busy]') : null;
        if (!form) { return; }

        // Seen already by an outer root — not a second submit, the same one.
        if (_handledSubmits.has(e)) { return; }
        _handledSubmits.add(e);

        // A genuine second submit while the first is still in flight. Pressing
        // Enter in a field bypasses the button's blocked pointer events.
        if (form.dataset.busyState === 'submitting') {
            e.preventDefault();
            return;
        }

        form.dataset.busyState = 'submitting';

        // The submit button is left enabled on purpose — a disabled control is
        // dropped from the posted form data.
        setBusy(e.submitter || form.querySelector('[type="submit"]'));
    }, { signal });

    scope.addEventListener('click', (e) => {
        const origin = eventOrigin(e);
        const control = origin ? origin.closest('[data-busy]') : null;

        // A click anywhere inside a data-busy form finds the form itself — leave
        // that to the submit handler. localName rather than instanceof, which is
        // false across realms when the markup is injected from another document.
        if (!control || control.localName === 'form') { return; }

        // The default action is already queued, so blocking pointer events now
        // stops repeat clicks without cancelling this navigation. Idempotent, so
        // nested roots both firing is harmless.
        setBusy(control);
    }, { signal });

    // Coming back to a restored page: the action finished, so nothing is running
    // any more and every control has to be usable again.
    window.addEventListener('pageshow', () => {
        scope.querySelectorAll('.' + BUSY_CLASS).forEach(clearBusy);
        scope.querySelectorAll('form[data-busy]').forEach(form => delete form.dataset.busyState);
    }, { signal });
}

// =============================================================================
// #endregion busy state
// =============================================================================


// =============================================================================
// #region Public API
// =============================================================================

/**
 * Initialises (or re-initialises) all CMS widgets within the given root element.
 * Pass a specific element (e.g. a Froala .fr-element node or shadow root)
 * to scope the search, or omit root to scan the full document.
 * Re-calling on an already-initialised element safely tears down the previous
 * listeners before attaching new ones — no duplicates, no stale handlers.
 *
 * Usage from the Froala editor on contentChanged:
 *   window.CmsWidgets.init(this.el);
 */
window.CmsWidgets = {
    init: function (root) {
        initCards(root);
        initHero(root);
        initShowcase(root);
        initAccordion(root);
        initBusyState(root);
    }
};

document.addEventListener('DOMContentLoaded', () => window.CmsWidgets.init());

// =============================================================================
// #endregion Public API
// =============================================================================
